<header class="relative px-4 pb-4 pt-2">
  <h1 class="text-2xl font-semibold text-greyscale-900">
    Custom Emission Factors
  </h1>
  <p class="text-sm text-greyscale-600">
    Review and validate custom emission factors created by clients
  </p>
</header>

<main class="p-4">
  <p-table
    #dt
    [value]="filteredFactors"
    [paginator]="true"
    [rows]="20"
    [globalFilterFields]="['name', 'source', 'company.name', 'category']"
    styleClass="p-datatable-sm"
  >
    <ng-template #caption>
      <div class="flex flex-wrap items-end gap-4">
        <div class="min-w-[140px]">
          <span class="mb-1 block text-sm font-medium">Status</span>
          <p-select
            [options]="validatedOptions"
            [(ngModel)]="validatedFilter"
            optionLabel="label"
            optionValue="value"
            placeholder="All"
            [style]="{ width: '100%' }"
          />
        </div>
        <div class="min-w-[160px]">
          <span class="mb-1 block text-sm font-medium">Category</span>
          <p-select
            [options]="categoryOptions"
            [(ngModel)]="categoryFilter"
            optionLabel="label"
            optionValue="value"
            placeholder="All categories"
            [style]="{ width: '100%' }"
          />
        </div>
        <div class="min-w-[180px]">
          <span class="mb-1 block text-sm font-medium">Company</span>
          <p-select
            [options]="companyOptions"
            [(ngModel)]="companyFilter"
            optionLabel="label"
            optionValue="value"
            placeholder="All companies"
            [style]="{ width: '100%' }"
          />
        </div>
        <div>
          <p-button
            icon="pi pi-filter-slash"
            (onClick)="resetFilters()"
            [text]="true"
            [rounded]="true"
            pTooltip="Reset filters"
          />
        </div>
        <div class="ml-auto min-w-[200px]">
          <span class="mb-1 block text-sm font-medium">Search</span>
          <p-iconfield iconPosition="left">
            <p-inputicon>
              <i class="pi pi-search"></i>
            </p-inputicon>
            <input
              pInputText
              type="text"
              [(ngModel)]="globalFilter"
              (ngModelChange)="dt.filterGlobal(globalFilter, 'contains')"
              placeholder="Search factors..."
              class="w-full"
            />
          </p-iconfield>
        </div>
      </div>
    </ng-template>

    <ng-template #header>
      <tr>
        <th>Name</th>
        <th>Category</th>
        <th>Company</th>
        <th>Value</th>
        <th>Source</th>
        <th>Validated</th>
        <th>Actions</th>
      </tr>
    </ng-template>

    <ng-template #emptymessage>
      <tr>
        <td colspan="7" class="py-12 text-center">
          <div class="flex flex-col items-center gap-4">
            <i class="pi pi-sliders-h text-6xl text-greyscale-300"></i>
            <div class="flex flex-col gap-1">
              <h3 class="text-lg font-semibold text-greyscale-700">
                No emission factors found
              </h3>
              <p class="text-sm text-greyscale-500">
                @if (globalFilter || validatedFilter !== null || categoryFilter || companyFilter) {
                  Try adjusting your search or filter criteria
                } @else {
                  No custom emission factors have been created yet
                }
              </p>
            </div>
          </div>
        </td>
      </tr>
    </ng-template>

    <ng-template #body let-factor>
      <tr class="h-14">
        <!-- Name -->
        <td>
          <div class="flex flex-col">
            <span class="font-medium">{{ factor.name }}</span>
            @if (factor.description) {
              <span
                class="max-w-48 truncate text-xs text-greyscale-500"
                [pTooltip]="factor.description"
              >
                {{ factor.description }}
              </span>
            }
          </div>
        </td>

        <!-- Category -->
        <td>
          <p-tag
            class="w-fit border text-xs"
            [ngClass]="getCategoryTag(factor.category).classes"
          >
            <i
              [class]="getCategoryTag(factor.category).icon"
              class="mr-1 text-xs"
            ></i>
            {{ getCategoryTag(factor.category).label }}
          </p-tag>
        </td>

        <!-- Company -->
        <td>
          <span class="text-sm">{{ factor.company.name }}</span>
        </td>

        <!-- Value -->
        <td>
          <span class="font-mono text-sm">{{ formatValue(factor) }}</span>
        </td>

        <!-- Source -->
        <td>
          <span
            class="block max-w-[200px] truncate text-sm text-greyscale-600"
            [pTooltip]="factor.source ?? ''"
            [tooltipDisabled]="!factor.source || factor.source.length <= 30"
            tooltipPosition="top"
          >
            {{ factor.source ?? "--" }}
          </span>
        </td>

        <!-- Validated -->
        <td>
          @if (factor.validated) {
            <p-tag
              class="w-fit border text-xs bg-success-50 text-success-800 border-success-200"
            >
              <i class="pi pi-check mr-1 text-xs"></i>
              Validated
            </p-tag>
          } @else {
            <p-tag
              class="w-fit border text-xs bg-orange-50 text-orange-800 border-orange-200"
            >
              <i class="pi pi-clock mr-1 text-xs"></i>
              Pending
            </p-tag>
          }
        </td>

        <!-- Actions -->
        <td>
          @if (factor.validated) {
            <p-button
              icon="pi pi-times"
              [text]="true"
              [rounded]="true"
              severity="danger"
              pTooltip="Remove validation"
              (onClick)="toggleValidation(factor)"
            />
          } @else {
            <p-button
              icon="pi pi-check"
              [text]="true"
              [rounded]="true"
              severity="success"
              pTooltip="Validate factor"
              (onClick)="toggleValidation(factor)"
            />
          }
        </td>
      </tr>
    </ng-template>
  </p-table>
</main>

<p-toast />
